/**
 * The shopper's own location, only after they tap "Use my location" (D-05, D-65). Used for
 * distances, nearest-first (BR-4) and the nearest town (BR-24); sent with the background logs
 * (D-77) and, rounded to ~100 m, with lists for nearby-first (BR-28, D-78).
 */
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

import type { LatLng } from '@/lib/geo'
import { persistStorage } from '@/lib/storage'
import type { ShopperLocation } from '@/types/api'

/** A browser position with how sure the phone is (metres), when it says. */
export type Located = LatLng & { accuracyM?: number | null }

interface LocationState {
  coords: Located | null
  /** The shopper tapped "Stop using my location": no start-up prompt or silent refresh (D-65). */
  optedOut: boolean
  setCoords: (coords: Located | null) => void
  stopUsing: () => void
}

export const useLocationStore = create<LocationState>()(
  persist(
    (set) => ({
      coords: null,
      optedOut: false,
      setCoords: (coords) => set(coords ? { coords, optedOut: false } : { coords }),
      stopUsing: () => set({ coords: null, optedOut: true }),
    }),
    { name: 'angadi.location', storage: createJSONStorage(() => persistStorage), version: 1 },
  ),
)

/** The shared position for a log body (D-77), or undefined when the shopper hasn't shared one. */
export function sharedLocation(): ShopperLocation | undefined {
  const c = useLocationStore.getState().coords
  return c ? { lat: c.lat, lng: c.lng, accuracy_m: c.accuracyM ?? null } : undefined
}

/** `near=lat,lng` for lists (BR-28, D-78): 3 decimals (~100 m), so edge caching still works. */
export const nearParam = (c: LatLng | null): string | undefined =>
  c ? `${c.lat.toFixed(3)},${c.lng.toFixed(3)}` : undefined

/** The `near` value for list queries; changes only when the shopper moves about 100 m. */
export const useNear = (): string | undefined => nearParam(useLocationStore((s) => s.coords))
